/**
 * Soft delete, browser side.
 *
 * Deleting a record stamps `deletedAt` instead of removing the row, so it can be
 * restored for PURGE_AFTER_DAYS; after that the nightly cleanup-deleted-records
 * function removes it for good. Every list of one of these tables filters with
 * `notDeleted()`. See .claude/skills/soft-delete/SKILL.md.
 *
 * Deliberately a second copy of the table list rather than a shared module: the
 * functions' copy lives in functions/cleanup-deleted-records/record-registry.js,
 * and CRA's ModuleScopePlugin rejects an import from outside src/ (the same
 * constraint utils/ticket-reference.js documents). lib/records.test.js pins this
 * list to appwrite.config.json, as the functions' test pins theirs, so the two
 * copies cannot disagree without a test failing.
 */

import { Query } from 'appwrite';

/** How long a deleted record can still be restored. Matches the purge. */
export const PURGE_AFTER_DAYS = 90;

/**
 * The tables that soft-delete — exactly those with a `deletedAt` column.
 *
 *   label       what a person calls one of these
 *   parent      the record this one belongs to. Restoring a child whose parent
 *               is itself deleted is refused: it would reappear nowhere.
 *   naturalKey  columns that allow only one live row per value. Restoring a row
 *               is refused when a live row already holds its key — otherwise a
 *               restored override or mapping would fight the one made since.
 *               `naturalKeyWhen` limits the rule to the rows it applies to.
 */
export const SOFT_DELETE_TABLES = {
  quote_requests: { label: 'Quote request' },
  quote_request_images: {
    label: 'Photo',
    parent: { table: 'quote_requests', field: 'quoteRequestId' },
  },
  notes: {
    label: 'Note',
    parent: { tableField: 'relatedRecordType', field: 'relatedRecordId' },
  },
  org_line_allowances: {
    label: 'Shop line',
    // Only an override has a key: one live override per standard line.
    naturalKey: ['organisationId', 'standardLineId'],
    naturalKeyWhen: (row) => Boolean(row.standardLineId),
  },
  customer_organisation_links: { label: 'Organisation link' },
  'panel-quote-insurer-map': {
    label: 'Insurer mapping',
    naturalKey: ['organisationId', 'insurerId'],
  },
  fleet_vehicles: { label: 'Fleet vehicle' },
  fleet_bookings: {
    label: 'Booking',
    parent: { table: 'fleet_vehicles', field: 'vehicle' },
  },
};

export function isSoftDeleteTable(table) {
  return Object.prototype.hasOwnProperty.call(SOFT_DELETE_TABLES, table);
}

/** The query that leaves deleted rows out. Existing rows hold null, so this matches them. */
export function notDeleted() {
  return Query.isNull('deletedAt');
}

export function isDeleted(row) {
  return Boolean(row && row.deletedAt);
}

/** When a deleted row is removed for good, or null for a live one. */
export function purgeDate(row) {
  if (!isDeleted(row)) return null;
  return new Date(new Date(row.deletedAt).getTime() + PURGE_AFTER_DAYS * 24 * 60 * 60 * 1000);
}

/** The parent a row points at, as `{ table, id }`, or null. Relationship values may arrive expanded. */
export function parentOf(table, row) {
  const spec = SOFT_DELETE_TABLES[table]?.parent;
  if (!spec || !row) return null;
  const parentTable = spec.table || row[spec.tableField];
  const value = row[spec.field];
  const id = value && typeof value === 'object' ? value.$id : value;
  return parentTable && id ? { table: parentTable, id } : null;
}

/** The natural-key values a row holds, or null when the table (or this row) has none. */
export function naturalKeyOf(table, row) {
  const spec = SOFT_DELETE_TABLES[table];
  if (!spec?.naturalKey || !row) return null;
  if (spec.naturalKeyWhen && !spec.naturalKeyWhen(row)) return null;
  return Object.fromEntries(spec.naturalKey.map((field) => [field, row[field]]));
}

// =====================================================
// ROW PERMISSIONS
// =====================================================

const GRANT = /^(\w+)\("([^"]+)"\)$/;

/** Does one role string, as Appwrite writes it, include this user? */
function roleIncludes(role, user) {
  if (role === 'any') return true;
  if (role === 'users') return Boolean(user?.id);
  if (role.startsWith('user:')) return role.slice(5) === user?.id;
  if (role.startsWith('team:')) {
    const [teamId, teamRole] = role.slice(5).split('/');
    if (teamRole) {
      // Team roles are only known for the organisation the user is working in.
      return teamId === user?.teamId && (user?.teamRoles || []).includes(teamRole);
    }
    return teamId === user?.teamId || (user?.teams || []).some((t) => (t.id || t) === teamId);
  }
  return false;
}

/**
 * Does the row's own `$permissions` let this user perform `action`?
 * `write` grants create, update and delete together.
 */
export function hasPermission(row, action, user) {
  return (row?.$permissions || []).some((grant) => {
    const match = GRANT.exec(grant);
    if (!match) return false;
    const [, kind, role] = match;
    return (kind === action || kind === 'write') && roleIncludes(role, user);
  });
}

/**
 * Whether this user may delete the row.
 *
 * A soft delete is an update as far as Appwrite knows, so Appwrite cannot hold
 * the row's `delete` permission any more — the admin app has to. Requests a
 * customer submits grant delete to the team owner only, and that rule survives
 * because Delete is offered only when this holds. A UI check, not a server one:
 * the same member can already edit every field, and a soft delete is undoable.
 */
export function canDelete(row, user) {
  return hasPermission(row, 'delete', user);
}

/** Whether this user may restore the row — anyone who can edit it. */
export function canUpdate(row, user) {
  return hasPermission(row, 'update', user);
}
